'use client'

import { motion } from 'framer-motion'
import { Cookie, Settings, Eye, Trash2, Shield, Info } from 'lucide-react'

const cookieTypes = [
  {
    title: 'Essential Cookies',
    icon: Shield,
    description: 'These cookies are necessary for the website to function properly.',
    examples: [
      'Session management and security',
      'Website functionality and navigation',
      'Load balancing and performance optimization'
    ],
    required: true
  },
  {
    title: 'Analytics Cookies',
    icon: Eye,
    description: 'Help us understand how visitors interact with our website.',
    examples: [
      'Page views and user behavior tracking',
      'Website performance monitoring',
      'Error reporting and debugging'
    ],
    required: false
  },
  {
    title: 'Functional Cookies',
    icon: Settings,
    description: 'Enable enhanced functionality and personalization.',
    examples: [
      'Language and region preferences',
      'User interface customizations',
      'Form data retention'
    ],
    required: false
  }
]

const sections = [
  {
    title: 'What Are Cookies?',
    icon: Info,
    content: [
      'Cookies are small text files stored on your device when you visit our website.',
      'They help us provide you with a better browsing experience and improve our services.',
      'Cookies contain information about your preferences and activities on our site.',
      'Most web browsers automatically accept cookies, but you can modify your settings to decline them.'
    ]
  },
  {
    title: 'How We Use Cookies',
    icon: Cookie,
    content: [
      'To ensure our website functions properly and securely',
      'To analyze website traffic and user behavior patterns',
      'To remember your preferences and settings',
      'To improve our website performance and user experience'
    ]
  },
  {
    title: 'Managing Your Cookie Preferences',
    icon: Settings,
    content: [
      'You can control cookie settings through your browser preferences.',
      'Most browsers allow you to view, delete, and block cookies.',
      'Disabling essential cookies may affect website functionality.',
      'You can opt out of analytics cookies while maintaining essential functionality.'
    ]
  },
  {
    title: 'Third-Party Cookies',
    icon: Eye,
    content: [
      'We may use third-party services that set their own cookies.',
      'These services help us analyze website performance and user engagement.',
      'Third-party cookies are governed by their respective privacy policies.',
      'We carefully select partners who share our commitment to privacy.'
    ]
  },
  {
    title: 'Cookie Retention',
    icon: Trash2,
    content: [
      'Session cookies are deleted when you close your browser.',
      'Persistent cookies remain until they expire or you delete them.',
      'We regularly review and update our cookie retention policies.',
      'You can delete cookies at any time through your browser settings.'
    ]
  }
]

export default function CookiePolicy() {
  return (
    <div className="min-h-screen bg-white">
      <main>
        {/* Hero Section */}
        <section className="pt-24 pb-16 px-4">
          <div className="container mx-auto max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                Cookie <span className="gradient-text">Policy</span>
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                Learn how Elyndra Studios uses cookies to enhance your browsing experience 
                and improve our services while respecting your privacy.
              </p>
              <div className="mt-8 text-sm text-gray-500">
                Last updated: March 7, 2025
              </div>
            </motion.div>
          </div>
        </section>

        {/* Cookie Types */}
        <section className="py-16 px-4 bg-gradient-to-br from-gray-50 to-blue-50">
          <div className="container mx-auto max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-bold mb-6">Types of Cookies We Use</h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                We use different types of cookies to provide you with the best possible experience on our website.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-1 lg:grid-cols-3 gap-8">
              {cookieTypes.map((type, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-2xl p-6 shadow-lg"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center">
                      <type.icon className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-800">{type.title}</h3>
                  </div>
                  <p className="text-gray-600 mb-4">{type.description}</p>
                  <ul className="space-y-2 mb-4">
                    {type.examples.map((example, exampleIndex) => (
                      <li key={exampleIndex} className="text-sm text-gray-600 flex items-start gap-2">
                        <span className="w-1.5 h-1.5 bg-primary-500 rounded-full mt-2 flex-shrink-0"></span>
                        {example}
                      </li>
                    ))}
                  </ul>
                  <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    type.required 
                      ? 'bg-red-100 text-red-700' 
                      : 'bg-green-100 text-green-700'
                  }`}>
                    {type.required ? 'Required' : 'Optional'}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Policy Sections */}
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-4xl">
            <div className="space-y-12">
              {sections.map((section, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="card"
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center flex-shrink-0">
                      <section.icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-800">{section.title}</h3>
                  </div>
                  <ul className="space-y-3">
                    {section.content.map((item, itemIndex) => (
                      <li key={itemIndex} className="text-gray-600 leading-relaxed flex items-start gap-3">
                        <span className="w-2 h-2 bg-primary-500 rounded-full mt-2 flex-shrink-0"></span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Browser Settings Guide */}
        <section className="py-16 px-4 bg-gradient-to-br from-primary-50 to-secondary-50">
          <div className="container mx-auto max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-bold mb-6">Browser Cookie Settings</h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                You can manage cookies through your browser settings. Here are quick links to cookie settings for popular browsers:
              </p>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {['Chrome', 'Firefox', 'Safari', 'Edge'].map((browser, index) => (
                  <motion.div
                    key={index}
                    className="bg-white rounded-xl p-4 shadow-md"
                    whileHover={{ scale: 1.05 }}
                  >
                    <h4 className="font-semibold text-gray-800 mb-2">{browser}</h4>
                    <p className="text-sm text-gray-600">Manage cookie preferences in {browser} settings</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="bg-gradient-to-r from-primary-500 to-secondary-500 rounded-3xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-4">Questions About Cookies?</h3>
                <p className="text-lg mb-6 opacity-90">
                  If you have any questions about our use of cookies or need help managing your preferences, 
                  please don&apos;t hesitate to contact us.
                </p>
                <div className="space-y-2 mb-6">
                  <p className="text-lg">Email: privacy@elyndrastudios.com</p>
                  <p className="text-lg">General: hello@elyndrastudios.com</p>
                </div>
                <motion.a
                  href="/#contact"
                  className="bg-white text-primary-600 font-semibold py-3 px-8 rounded-2xl hover:bg-gray-50 transition-colors inline-flex items-center"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Contact Us
                </motion.a>
              </div>
            </motion.div>
          </div>
        </section>
      </main>
    </div>
  )
}
